import * as React from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { enrichCatalogResults } from 'bundles/browse/components/CatalogResultUtils';
import { mapLOHPCollectionForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
import NativeError from 'bundles/ent-website/contentful/modules/native/NativeError';
import type {
  CollectionsQuery,
  CollectionsQueryVariables,
} from 'bundles/ent-website/contentful/modules/native/__generated__/CollectionsQuery';
import { CollectionsFragment } from 'bundles/front-page/common/Queries';

// copied from bundles/frontpage/common/Queries
const colelctionsQuery = gql`
  query CollectionsQuery($contextType: String!, $contextId: String!, $numEntriesPerCollection: Int!) {
    BrowseCollectionsV1Resource {
      byCollections(
        contextType: $contextType
        contextId: $contextId
        numEntriesPerCollection: $numEntriesPerCollection
      ) {
        ...CollectionsFragment
      }
    }
  }

  ${CollectionsFragment}
`;

type Props = {
  args?: {
    context?: string;
    contextId?: string;
    numEntriesPerCollection?: number;
  };
};

function Collections({ args }: Props) {
  const { data, loading } = useQuery<CollectionsQuery, CollectionsQueryVariables>(colelctionsQuery, {
    variables: {
      contextType: args?.context ?? '',
      contextId: args?.contextId ?? '',
      numEntriesPerCollection: args?.numEntriesPerCollection ?? 4,
    },

    skip: !(args?.context && args?.contextId),
  });

  // verify arguments
  let errMsg = '';
  if (!args) {
    errMsg = '[Collections] Missing required arguments';
  } else {
    const missingArgs: string[] = [];
    if (!args.context) {
      missingArgs.push('context');
    }
    if (!args.contextId) {
      missingArgs.push('contextId');
    }

    errMsg = missingArgs.length > 0 ? `[Collections] Missing required arguments: ${missingArgs.join(', ')}` : '';
  }

  if (errMsg) {
    return <NativeError msg={errMsg} />;
  }

  // Fetch the collections
  const isNotNull = <T,>(el: T | null): el is T => el !== null;
  const collections = (data?.BrowseCollectionsV1Resource.byCollections?.elements ?? []).filter(isNotNull);

  if (!loading && collections.length === 0) {
    return (
      <NativeError
        msg={`[Collections] No collections found with context=${args?.context}, contextId=${args?.contextId}`}
      />
    );
  }

  // @ts-expect-error(apollo-v3): TS2345: Argument of type 'LOHPCollectionQuery_BrowseCollectionsV1Resource_target
  const enrichedCollections = enrichCatalogResults(collections);
  const carouselCollections = enrichedCollections
    .map((collection, index) => mapLOHPCollectionForCarousel(collection, index))
    .filter(isNotNull);

  return <CollectionsContainer collections={carouselCollections} isCollectionsLoading={loading} />;
}

export default Collections;
